import submit from "submit-form";
import moment from "moment";
import round from "lodash/round";

const nutrientRegex = /^[0-9]{1}\d*\.?(?:\d{1})?$/;

export const validateNutritionPlan = (
  plan,
  editPlan,
  coachAvatar,
  firebaseProjectId,
  assert,
  flash,
  nutritionPlans
) => {
  try {
    const {
      calories,
      proteins,
      carbs,
      fats,
      fibers,
      startDate,
      endDate,
      startingVar,
      proteinsPercent,
      carbsPercent,
      fatsPercent,
      restDayActive,
      restDayProteins,
      restDayCarbs,
      restDayFats,
      restDayFibers,
      restDayCalories,
      restDayProteinsPercent,
      restDayCarbsPercent,
      restDayFatsPercent,
      notes,
    } = plan;
    const _id = editPlan && editPlan._id;

    assert(startDate, 400, "Start date is required");
    assert(endDate, 400, "End date is required");
    assert(
      !moment(endDate).isSameOrBefore(moment(startDate)),
      400,
      "End date must be after start date"
    );
    assert(
      !nutritionPlanOverlaps(plan, _id, nutritionPlans),
      400,
      "A plan already exists in the selected date range"
    );

    assert(proteins, 400, "Protein is required");
    assert(
      nutrientRegex.test(proteins),
      400,
      "Protein must be a positive number"
    );
    assert(carbs, 400, "Carbs are required");
    assert(nutrientRegex.test(carbs), 400, "Carbs must be a positive number");
    assert(fats, 400, "Fat is required.");
    assert(nutrientRegex.test(fats), 400, "Fat must be a positive number");
    assert(calories, 400, "Calories are required");
    assert(
      nutrientRegex.test(calories),
      400,
      "Calories must be a positive number"
    );

    if (fibers) {
      assert(
        nutrientRegex.test(fibers),
        400,
        "Fiber must be a positive number"
      );
    }

    if (startingVar === "kcal") {
      assert(proteinsPercent, 400, "Protein % is required");
      assert(carbsPercent, 400, "Carbs % is required");
      assert(fatsPercent, 400, "Fat % is required");

      const pfcPercent = round(
        Number(proteinsPercent) + Number(carbsPercent) + Number(fatsPercent),
        1
      );
      const pfcDelta = round(pfcPercent - 100, 1);

      assert(!(pfcDelta > 0), 400, `PFC% exceeds kCal target by ${pfcDelta}%`);
      assert(
        !(pfcDelta < 0),
        400,
        `PFC% is down on kCal target by ${pfcDelta}%`
      );
    }

    if (restDayActive) {
      assert(restDayProteins, 400, "Rest Day Protein is required");
      assert(
        nutrientRegex.test(restDayProteins),
        400,
        "Rest Day Protein must be a positive number"
      );
      assert(restDayCarbs, 400, "Rest Day Carbs are required");
      assert(
        nutrientRegex.test(restDayCarbs),
        400,
        "Rest Day Carbs must be a positive number"
      );
      assert(restDayFats, 400, "Rest Day Fat is required.");
      assert(
        nutrientRegex.test(restDayFats),
        400,
        "Rest Day Fat must be a positive number"
      );
      assert(restDayCalories, 400, "Rest Day Calories are required");
      assert(
        nutrientRegex.test(restDayCalories),
        400,
        "Rest Day Calories must be a positive number"
      );

      if (restDayFibers) {
        assert(
          nutrientRegex.test(restDayFibers),
          400,
          "Rest Day Fiber must be a positive number"
        );
      }

      if (startingVar === "kcal") {
        assert(restDayProteinsPercent, 400, "Rest Day Protein % is required");
        assert(restDayCarbsPercent, 400, "Rest Day Carbs % is required");
        assert(restDayFatsPercent, 400, "Rest Day Fat % is required");

        const pfcPercent = round(
          Number(restDayProteinsPercent) +
            Number(restDayCarbsPercent) +
            Number(restDayFatsPercent),
          1
        );
        const pfcDelta = round(pfcPercent - 100, 1);

        assert(
          !(pfcDelta > 0),
          400,
          `PFC% exceeds kCal target by ${pfcDelta}% on Rest Day`
        );
        assert(
          !(pfcDelta < 0),
          400,
          `PFC% is down on kCal target by ${pfcDelta}% on Rest Day`
        );
      }
    }

    const body = {
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
      calories: Number(calories),
      proteins: Number(proteins),
      carbs: Number(carbs),
      fats: Number(fats),
      avatar: coachAvatar,
      firebaseProjectId: firebaseProjectId,
    };

    if (fibers) {
      body.fibers = Number(fibers);
    }

    if (restDayActive) {
      body.restDay = {
        proteins: restDayProteins,
        carbs: restDayCarbs,
        fats: restDayFats,
        calories: restDayCalories,
      };

      if (restDayFibers) {
        body.restDay.fibers = restDayFibers;
      }
    } else {
      body.restDay = null;
    }

    if (notes) {
      body.notes = notes;
    }

    return body;
  } catch (err) {
    flash("error", err.message);
    submit("/actions/refresh");
    return false;
  }
};

const nutritionPlanOverlaps = (plan, id = null, list) => {
  // filter out the plan being edited
  const filtered = id ? list.filter(({ _id }) => _id !== id) : list;

  const start = Number(moment(plan.startDate).format("YYYYMMDD"));
  const end = Number(moment(plan.endDate).format("YYYYMMDD"));

  const overlapPlan = filtered.find((item) => {
    if (start >= item.utcStartDate && start <= item.utcEndDate) {
      // submitted plan start overlaps
      return true;
    } else if (end >= item.utcStartDate && end <= item.utcEndDate) {
      // submitted plan end overlaps
      return true;
    } else if (start <= item.utcStartDate && end >= item.utcEndDate) {
      // a plan exists between the submitted plan's date range
      return true;
    }
    return false;
  });

  return !!overlapPlan;
};
